'use client';

import { useState } from 'react';
import { Calendar, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { BaptismInfo } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardHero } from './CardHero';
import { CardCTA } from './CardCTA';

interface BaptismCardProps {
  baptism: BaptismInfo;
}

export function BaptismCard({ baptism }: BaptismCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const nextDate = baptism.Upcoming_Dates[0];

  return (
    <>
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        header={
          <CardHero
            bgUrl={baptism.Hero_Image_URL}
            dateStrip={
              nextDate ? `Next: ${nextDate.Date} · ${nextDate.Congregation_Name}` : undefined
            }
          />
        }
        body={
          <div className="p-4 pb-3">
            <h3 className="text-foreground text-base leading-tight font-extrabold">Baptism</h3>
            <p className="text-muted-foreground mt-0.5 text-xs font-semibold tracking-wider uppercase">
              Get Baptized
            </p>
            <p className="text-muted-foreground mt-2 line-clamp-2 text-sm leading-relaxed">
              {baptism.Tagline}
            </p>
          </div>
        }
        footer={<CardCTA label="Learn More" useDefaultColors />}
      />

      {/* Detail sheet */}
      <ResponsiveSheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName="bg-card"
        header={
          <div
            className="relative overflow-hidden border-b border-white/10"
            style={{ backgroundColor: '#1c2b39' }}
          >
            {/* Watermark */}
            <Img
              src="/Baptism/BaptismLogo.svg"
              alt=""
              className="pointer-events-none absolute top-1/2 right-4 h-[80%] -translate-y-1/2 opacity-[0.06]"
              style={{ filter: 'invert(1)' }}
            />
            <div className="relative z-10 flex items-end gap-4 p-5">
              {/* Photo */}
              <div className="h-24 w-24 shrink-0 overflow-hidden sm:h-28 sm:w-28">
                <Img
                  src={baptism.Hero_Image_URL}
                  alt="Baptism"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 h-1 w-12 bg-[#61bc47]" />
                <h2 className="text-xl leading-tight font-light tracking-wide text-white sm:text-2xl">
                  baptism
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-white/50">{baptism.Tagline}</p>
              </div>
            </div>
          </div>
        }
      >
        <SheetPage name="main">
          <div>
            <div className="space-y-6 p-5">
              {/* Description */}
              <div>
                <h3 className="text-foreground text-base font-bold">What is Baptism?</h3>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                  {baptism.Description}
                </p>
              </div>

              {/* Upcoming dates */}
              {baptism.Upcoming_Dates.length > 0 && (
                <div>
                  <h3 className="text-foreground text-base font-bold">Upcoming Baptisms</h3>
                  <div className="mt-3 space-y-2">
                    {baptism.Upcoming_Dates.map((d) => (
                      <a
                        key={d.ID}
                        href={d.Sign_Up_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group border-border bg-background flex items-center justify-between gap-3 border p-3 transition-shadow hover:shadow-md"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#61bc47]/10">
                            <Calendar className="h-4 w-4 text-[#61bc47]" />
                          </div>
                          <div>
                            <p className="text-foreground text-sm font-semibold">{d.Date}</p>
                            <p className="text-muted-foreground text-xs">
                              {d.Time} · {d.Congregation_Name}
                              {d.Context && ` · ${d.Context}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-[#61bc47] uppercase">
                          Sign Up
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ */}
              {baptism.FAQ.length > 0 && (
                <div>
                  <h3 className="text-foreground text-base font-bold">Common Questions</h3>
                  <div className="mt-3 space-y-1">
                    {baptism.FAQ.map((faq, i) => (
                      <FAQItem key={i} question={faq.Question} answer={faq.Answer} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Learn More CTA */}
            <a
              href={baptism.Inquiry_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-gray-900 uppercase transition-opacity hover:opacity-80"
            >
              Learn More
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// FAQ Accordion Item
// ---------------------------------------------------------------------------

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-border border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between py-3 text-left"
      >
        <span className="text-foreground text-sm font-medium">{question}</span>
        <ChevronDown
          className={`text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-muted-foreground pb-3 text-sm leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
